import { classNames, Component, h, signal, tag } from "omi";

import { EventDataType } from "@/services/analyticsService";
import { tailwind } from "@/tailwind";

import { NWButton } from "../atoms/nw-button";
import { Feedback } from "./models";

type Props = {
  label?: string;
  /**
   * The feature that the feedback is related to
   */
  feature: string;

  /**
   * Additional ptions to pass to the analytics service
   */
  analyticsOptions?: { [key: string]: unknown };
};

@tag("nw-feedback")
export class NWFeedback extends Component<Props> {
  static css = [tailwind];

  static propTypes = {
    label: String,
  };

  static defaultProps = {
    label: "How did we do? Was the response good or unusual?",
  };

  private feedbackValue = signal<Feedback | undefined>(undefined);
  private showMessage = signal<boolean>(true);

  private handleClick = (feedback: Feedback, event: MouseEvent) => {
    event.preventDefault();
    if (this.feedbackValue.peek() != null) {
      return;
    }

    this.feedbackValue.value = feedback;
    this.showMessage.value = true;
    this.track(this.props.feature, feedback, this.props.analyticsOptions);

    setTimeout(() => {
      this.showMessage.value = false;
    }, 5000);
  };

  private track = (feature: string, feedback: Feedback, analyticsOptions?: EventDataType) => {
    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackFeedbackClicked(feature, feedback, analyticsOptions);
    });
  };

  render({ label = "How did we do? Was the response good or unusual?" }: Props) {
    const hasAnswered = this.feedbackValue.value != null;
    const showFeedbackMessage = hasAnswered && this.showMessage.value;

    return (
      <div class={classNames("flex flex-row items-center h-9 space-x-2")}>
        <div className={classNames("flex flex-row", { "gap-0.5": !hasAnswered })}>
          <div
            className={classNames("transition-transform duration-200 ease-in-out relative", {
              "opacity-0 pointer-events-none !w-0 overflow-hidden": this.feedbackValue.value == Feedback.BAD,
              "opacity-100 active:scale-95 hover:scale-110": !hasAnswered,
            })}
          >
            <NWButton
              size="icon"
              icon="thumbs-up"
              iconPadding="sm"
              type="icon"
              onClick={e => this.handleClick(Feedback.GOOD, e)}
              disabled={hasAnswered}
              backgroundTransparent
            />
          </div>
          <div
            className={classNames("transition-transform duration-200 ease-in-out", {
              "opacity-0 pointer-events-none !w-0 overflow-hidden": this.feedbackValue.value == Feedback.GOOD,
              "opacity-100 active:scale-95 hover:scale-110": !hasAnswered,
            })}
          >
            <NWButton
              size="icon"
              icon="thumbs-down"
              iconPadding="sm"
              type="icon"
              onClick={e => this.handleClick(Feedback.BAD, e)}
              disabled={hasAnswered}
              backgroundTransparent
            />
          </div>
        </div>
        <p
          className={classNames("text-sm text-tertiary transition-opacity ease-in-out", {
            "animate-fadeInFast": showFeedbackMessage,
            "opacity-0": !showFeedbackMessage,
          })}
        >
          {hasAnswered ? "Thanks for your feedback!" : label}
        </p>
      </div>
    );
  }
}
